import { http } from '@/lib/api-client'
import type {
  Teacher,
  TeacherDetail,
  TeacherCounts,
  TeacherRosterCourse,
  CreateTeacherInput,
  UpdateTeacherInput,
  TeacherListResponse,
  ListTeachersParams,
  TeacherAccessResult,
  TeacherPasswordResult,
} from './teachers.types'

// CRUD for teachers — /api/teachers. Branch-scoped by the lesson.
// list → full paginated envelope; single → unwrapped entity.
export const teacherApi = {
  list: (params?: ListTeachersParams): Promise<TeacherListResponse> =>
    http.get<TeacherListResponse>('/teachers', { params }),

  // Per-status head-counts for the status tab badges.
  counts: (): Promise<TeacherCounts> =>
    http.get<{ data: TeacherCounts }>('/teachers/counts').then((r) => r.data),

  getById: (id: string): Promise<TeacherDetail> =>
    http.get<{ data: TeacherDetail }>(`/teachers/${id}`).then((r) => r.data),

  // The teacher's courses + active enrollments (Cursos/Estudiantes tabs).
  roster: (id: string): Promise<TeacherRosterCourse[]> =>
    http
      .get<{ data: TeacherRosterCourse[] }>(`/teachers/${id}/roster`)
      .then((r) => r.data),

  create: (data: CreateTeacherInput): Promise<Teacher> =>
    http.post<{ data: Teacher }>('/teachers', data).then((r) => r.data),

  // PATCH (not PUT).
  update: (id: string, data: UpdateTeacherInput): Promise<Teacher> =>
    http.patch<{ data: Teacher }>(`/teachers/${id}`, data).then((r) => r.data),

  updateWithPhoto: (id: string, data: UpdateTeacherInput, image: File): Promise<Teacher> => {
    const body = new FormData()
    body.set('data', JSON.stringify(data))
    body.set('image', image)
    return http.patch<{ data: Teacher }>(`/teachers/${id}`, body, {
      headers: { 'Content-Type': undefined },
      timeout: 60_000,
    }).then((r) => r.data)
  },

  delete: (id: string): Promise<void> => http.delete(`/teachers/${id}`),

  // --- Acceso al portal del profesor ("Mi aula") -------------------------
  // Crea (o reutiliza) la cuenta con el correo del profesor y lo agrega a la
  // sucursal como miembro `teacher`. Solo administradores.
  grantAccess: (id: string, password?: string): Promise<TeacherAccessResult> =>
    http
      .post<{ data: TeacherAccessResult }>(`/teachers/${id}/access`, {
        ...(password && { password }),
      })
      .then((r) => r.data),

  resetAccessPassword: (
    id: string,
    password?: string,
  ): Promise<TeacherPasswordResult> =>
    http
      .post<{ data: TeacherPasswordResult }>(`/teachers/${id}/access/password`, {
        ...(password && { password }),
      })
      .then((r) => r.data),

  revokeAccess: (id: string): Promise<void> =>
    http.delete(`/teachers/${id}/access`),
}
